import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from '../contexts/ToastContext';
import { useTemplates } from '../hooks/useTemplates';
import {
  useCampaign,
  useCreateCampaign,
  useUpdateCampaign,
  usePreviewAudience,
  useAudienceFields
} from '../hooks/useCampaigns';
import ConditionBuilder from '../components/admin/email/ConditionBuilder';
import { generateUtmCampaignSlug } from '../utils/utm';
import {
  shouldWarnInactiveTemplate,
  INACTIVE_TEMPLATE_MESSAGE
} from '../utils/campaignTemplate';
import { AlertTriangle } from 'lucide-react';
import './CampaignEditor.css';

// Field groups for audience targeting
/**
 * Build the targeting fields from the values that actually exist in the data.
 *
 * The previous list was inherited from a multi-property rental model: language,
 * timezone and per-property/per-city targeting. There is one house, and
 * email_preferences is empty, so none of those could ever match. These are the
 * axes the schema can really answer.
 */
function buildAudienceFieldGroups(options) {
  const asOptions = (values) => (values || []).map((v) => ({ value: v, label: v }));

  const whoTheyAre = [
    {
      field: 'customer.type',
      label: 'Member type',
      type: 'enum',
      options: asOptions(options?.types)
    },
    {
      field: 'customer.family',
      label: 'Family branch',
      type: 'enum',
      options: asOptions(options?.families)
    },
    { field: 'customer.owner', label: 'Is an owner', type: 'boolean' },
    {
      field: 'customer.role',
      label: 'Account role',
      type: 'enum',
      options: asOptions(options?.roles)
    }
  ];

  const groups = [{ name: 'Who they are', fields: whoTheyAre }];

  if (options?.groups?.length) {
    groups.push({
      name: 'User groups',
      fields: [
        {
          field: 'customer.groupIds',
          label: 'User group',
          type: 'list',
          options: options.groups.map((g) => ({ value: g.id, label: g.name }))
        }
      ]
    });
  }

  groups.push({
    name: 'Relationship to the house',
    fields: [
      { field: 'reservation.hasEverStayed', label: 'Has ever stayed', type: 'boolean' },
      { field: 'reservation.hasUpcomingStay', label: 'Has an upcoming stay', type: 'boolean' },
      {
        field: 'reservation.daysSinceLastStay',
        label: 'Days since last stay',
        type: 'number'
      }
    ]
  });

  return groups;
}

function CampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isNew = !id;

  const { data: campaign, isLoading: campaignLoading } = useCampaign(isNew ? null : id);
  const { data: templatesData, isLoading: templatesLoading } = useTemplates();
  const createCampaign = useCreateCampaign();
  const updateCampaign = useUpdateCampaign(id);
  const previewAudience = usePreviewAudience(isNew ? null : id);
  const { data: audienceOptions } = useAudienceFields();

  const [audienceCondition, setAudienceCondition] = useState(null);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [showScheduleFields, setShowScheduleFields] = useState(false);
  const [showSendWindow, setShowSendWindow] = useState(false);

  const { register, handleSubmit, watch, setValue, reset, formState: { isDirty } } = useForm({
    defaultValues: {
      name: '',
      templateId: '',
      scheduleType: 'immediate',
      scheduleAt: '',
      sendWindowStart: '09:00',
      sendWindowEnd: '12:00',
      sendWindowTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      frequencyCapDays: 0,
      utmSource: 'email',
      utmMedium: 'campaign',
      utmCampaign: ''
    }
  });

  const campaignName = watch('name');
  const scheduleType = watch('scheduleType');
  const utmSource = watch('utmSource');
  const utmMedium = watch('utmMedium');
  const utmCampaign = watch('utmCampaign');
  const frequencyCapDays = watch('frequencyCapDays');

  const audienceFieldGroups = useMemo(
    () => buildAudienceFieldGroups(audienceOptions),
    [audienceOptions]
  );

  // Load campaign data
  useEffect(() => {
    if (campaign && !isNew) {
      reset({
        name: campaign.name,
        templateId: campaign.template_id,
        scheduleType: campaign.schedule_at ? 'scheduled' : 'immediate',
        scheduleAt: campaign.schedule_at ? new Date(campaign.schedule_at).toISOString().slice(0, 16) : '',
        sendWindowStart: campaign.send_window_start || '09:00',
        sendWindowEnd: campaign.send_window_end || '12:00',
        sendWindowTz: campaign.send_window_tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
        frequencyCapDays: campaign.frequency_cap_days || 0,
        utmSource: campaign.utm_source || 'email',
        utmMedium: campaign.utm_medium || 'campaign',
        utmCampaign: campaign.utm_campaign || ''
      });
      setAudienceCondition(campaign.audience_condition);
      setShowSendWindow(!!(campaign.send_window_start && campaign.send_window_end));
    }
  }, [campaign, isNew, reset]);

  // Auto-generate UTM campaign slug from name
  useEffect(() => {
    if (campaignName && !utmCampaign) {
      setValue('utmCampaign', generateUtmCampaignSlug(campaignName));
    }
  }, [campaignName, utmCampaign, setValue]);

  const handlePreviewAudience = async () => {
    try {
      const result = await previewAudience.mutateAsync({
        audienceCondition,
        frequencyCapDays: parseInt(frequencyCapDays, 10) || 0
      });
      setAudiencePreview(result);
      toast.success(`${result.estimatedCount} will receive this campaign`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to preview audience');
      console.error(error);
    }
  };

  // A preview describes one specific condition. Once the admin edits the
  // targeting or the cap, the old numbers are wrong, so clear them.
  useEffect(() => {
    setAudiencePreview(null);
  }, [audienceCondition, frequencyCapDays]);

  const onSubmit = async (data) => {
    if (!audienceCondition) {
      toast.error('Please define audience targeting conditions');
      return;
    }

    const payload = {
      name: data.name,
      templateId: data.templateId,
      audienceCondition,
      scheduleAt: data.scheduleType === 'scheduled' ? new Date(data.scheduleAt).toISOString() : null,
      sendWindowStart: showSendWindow ? data.sendWindowStart : null,
      sendWindowEnd: showSendWindow ? data.sendWindowEnd : null,
      sendWindowTz: showSendWindow ? data.sendWindowTz : null,
      frequencyCapDays: parseInt(data.frequencyCapDays, 10) || 0,
      utmSource: data.utmSource,
      utmMedium: data.utmMedium,
      utmCampaign: data.utmCampaign
    };

    try {
      if (isNew) {
        const newCampaign = await createCampaign.mutateAsync(payload);
        toast.success('Campaign created');
        navigate(`/campaigns/${newCampaign.id}/edit`, { replace: true });
      } else {
        await updateCampaign.mutateAsync(payload);
        toast.success('Campaign updated');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save campaign');
      console.error(error);
    }
  };

  const activeTemplates = templatesData?.data?.filter(t => t.is_active) || [];

  // Group templates by category
  const templatesByCategory = activeTemplates.reduce((acc, template) => {
    const cat = template.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(template);
    return acc;
  }, {});

  const utmPreview = `?utm_source=${utmSource}&utm_medium=${utmMedium}&utm_campaign=${utmCampaign}`;

  if (campaignLoading || templatesLoading) {
    return <div className="campaign-editor-loading">Loading...</div>;
  }

  if (!isNew && !campaign) {
    return <div className="campaign-editor-error">Campaign not found</div>;
  }

  if (!isNew && campaign.status !== 'draft') {
    return <div className="campaign-editor-error">Only draft campaigns can be edited</div>;
  }

  return (
    <div className="campaign-editor">
      <header className="editor-header">
        <h1>{isNew ? 'New Campaign' : `Edit: ${campaign.name}`}</h1>
      </header>

      {shouldWarnInactiveTemplate(campaign) && (
        <div className="editor-warning" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{INACTIVE_TEMPLATE_MESSAGE}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="editor-form">
        {/* Section 1: Basics */}
        <section className="form-section">
          <h2>Basics</h2>

          <div className="form-field">
            <label htmlFor="name">Campaign Name *</label>
            <input
              type="text"
              id="name"
              {...register('name', { required: true })}
              placeholder="e.g., Valentine's Day Special 2026"
              className="form-input"
            />
          </div>

          <div className="form-field">
            <label htmlFor="templateId">Email Template *</label>
            <select
              id="templateId"
              {...register('templateId', { required: true })}
              className="form-select"
            >
              <option value="">Select a template...</option>
              {Object.entries(templatesByCategory).map(([category, templates]) => (
                <optgroup key={category} label={category}>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </section>

        {/* Section 2: Audience */}
        <section className="form-section">
          <h2>Audience</h2>

          <div className="form-field">
            <label>Targeting Conditions *</label>
            <ConditionBuilder
              condition={audienceCondition}
              onChange={setAudienceCondition}
              groups={audienceFieldGroups}
            />
          </div>

          <button
            type="button"
            onClick={handlePreviewAudience}
            className="btn-secondary"
            disabled={previewAudience.isPending}
          >
            {previewAudience.isPending ? 'Loading...' : 'Preview Audience'}
          </button>

          {audiencePreview && (
            <div className="audience-preview">
              <h3>{audiencePreview.estimatedCount} will receive this campaign</h3>
              <ul className="audience-breakdown">
                <li>{audiencePreview.matchedCount} match your conditions</li>
                {audiencePreview.cappedCount > 0 && (
                  <li>&minus;{audiencePreview.cappedCount} recently emailed (frequency cap)</li>
                )}
                {audiencePreview.optedOutCount > 0 && (
                  <li>&minus;{audiencePreview.optedOutCount} opted out of marketing email</li>
                )}
              </ul>
              {audiencePreview.sampleRecipients.length > 0 && (
                <div className="sample-recipients">
                  <p>Sample recipients:</p>
                  <ul>
                    {audiencePreview.sampleRecipients.map((recipient, idx) => (
                      <li key={idx}>
                        {recipient.name} ({recipient.email})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Section 3: Schedule & Delivery */}
        <section className="form-section">
          <h2>Schedule & Delivery</h2>

          <div className="form-field">
            <label>When to send</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  value="immediate"
                  {...register('scheduleType')}
                  onChange={() => setShowScheduleFields(false)}
                />
                Send immediately
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  value="scheduled"
                  {...register('scheduleType')}
                  onChange={() => setShowScheduleFields(true)}
                />
                Schedule for later
              </label>
            </div>
          </div>

          {(showScheduleFields || scheduleType === 'scheduled') && (
            <div className="form-field">
              <label htmlFor="scheduleAt">Send Date & Time</label>
              <input
                type="datetime-local"
                id="scheduleAt"
                {...register('scheduleAt')}
                className="form-input"
              />
            </div>
          )}

          <div className="form-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showSendWindow}
                onChange={(e) => setShowSendWindow(e.target.checked)}
              />
              Use send window
            </label>
            <p className="field-hint">
              Spread sends across this window to avoid overwhelming your email provider
            </p>
          </div>

          {showSendWindow && (
            <div className="send-window-fields">
              <div className="form-field">
                <label htmlFor="sendWindowStart">Window Start</label>
                <input
                  type="time"
                  id="sendWindowStart"
                  {...register('sendWindowStart')}
                  className="form-input"
                />
              </div>

              <div className="form-field">
                <label htmlFor="sendWindowEnd">Window End</label>
                <input
                  type="time"
                  id="sendWindowEnd"
                  {...register('sendWindowEnd')}
                  className="form-input"
                />
              </div>

              <div className="form-field">
                <label htmlFor="sendWindowTz">Timezone</label>
                <input
                  type="text"
                  id="sendWindowTz"
                  {...register('sendWindowTz')}
                  className="form-input"
                  placeholder="America/New_York"
                />
              </div>
            </div>
          )}

          <div className="form-field">
            <label htmlFor="frequencyCapDays">Frequency Cap (days)</label>
            <input
              type="number"
              id="frequencyCapDays"
              {...register('frequencyCapDays')}
              min="0"
              className="form-input"
              placeholder="0 = no limit"
            />
            <p className="field-hint">
              Don't send to customers who received a campaign in the last N days (0 = no cap)
            </p>
          </div>
        </section>

        {/* Section 4: UTM Tracking */}
        <section className="form-section">
          <h2>UTM Tracking</h2>

          <div className="form-field">
            <label htmlFor="utmSource">UTM Source</label>
            <input
              type="text"
              id="utmSource"
              {...register('utmSource')}
              className="form-input"
              placeholder="email"
            />
          </div>

          <div className="form-field">
            <label htmlFor="utmMedium">UTM Medium</label>
            <input
              type="text"
              id="utmMedium"
              {...register('utmMedium')}
              className="form-input"
              placeholder="campaign"
            />
          </div>

          <div className="form-field">
            <label htmlFor="utmCampaign">UTM Campaign</label>
            <input
              type="text"
              id="utmCampaign"
              {...register('utmCampaign')}
              className="form-input"
              placeholder="Auto-generated from campaign name"
            />
          </div>

          <div className="utm-preview">
            <strong>Links will include:</strong>
            <code>{utmPreview}</code>
          </div>
        </section>

        {/* Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/campaigns')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!isDirty && !isNew}
          >
            {isNew ? 'Create Draft' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CampaignEditor;
